import type { MDXComponents } from 'mdx/types';

/** Components map passed to an MDX post for custom tags (see components/mdx). */
export type { MDXComponents };

/** Frontmatter contract for content/blog/*.mdx (see content-engine `publish`). */
export interface BlogFrontmatter {
  title: string;
  description: string;
  /** ISO date, e.g. "2026-06-18" */
  date: string;
  /** ISO date of last meaningful update; defaults to `date`. Freshness signal. */
  dateModified?: string;
  author?: string;
  tags?: string[];
  /** absolute or root-relative OG image url */
  ogImage?: string;
  /** primary target keyword (internal/SEO bookkeeping) */
  keyword?: string;
  /** set true to keep a post out of the published list */
  draft?: boolean;
}

/** A post without its compiled body: everything a loader, head or card needs. */
export interface BlogPostMeta {
  slug: string;
  frontmatter: BlogFrontmatter;
}

// Frontmatter ONLY, through the `?frontmatter` module that
// `scripts/mdxFrontmatter.ts` serves. That separation is load-bearing: a
// route's `loader`/`head` is never code-split (only `component` is), so a
// loader reaching for a post used to drag every compiled article into the entry
// chunk that all 200-plus pages share. Bodies live in `posts.ts`.
//
// Note the query rather than `{ import: 'frontmatter' }` on the .mdx module
// itself: that form still ships the bodies, because Rollup will not drop the
// unused compiled component from a module it cannot prove side-effect-free.
const frontmatters = import.meta.glob<Partial<BlogFrontmatter>>('/content/blog/*.mdx', {
  eager: true,
  query: '?frontmatter',
  import: 'default',
});

/** `/content/blog/foo.mdx` -> `foo`. */
export function slugFromPath(path: string): string {
  return path.split('/').pop()!.replace(/\.mdx$/, '');
}

/** Fills the frontmatter contract's defaults. The one owner of those defaults. */
export function normalizeFrontmatter(
  fm: Partial<BlogFrontmatter> | undefined,
  slug: string,
): BlogFrontmatter {
  return {
    title: fm?.title ?? slug,
    description: fm?.description ?? '',
    date: fm?.date ?? '1970-01-01',
    dateModified: fm?.dateModified,
    author: fm?.author,
    tags: fm?.tags ?? [],
    ogImage: fm?.ogImage,
    keyword: fm?.keyword,
    draft: fm?.draft ?? false,
  };
}

/** Published posts, newest first. Drafts never reach a list. */
export function sortPublished<T extends BlogPostMeta>(posts: T[]): T[] {
  return posts
    .filter((p) => !p.frontmatter.draft)
    .sort((a, b) => (a.frontmatter.date < b.frontmatter.date ? 1 : -1));
}

const ALL: BlogPostMeta[] = sortPublished(
  Object.entries(frontmatters).map(([path, fm]) => {
    const slug = slugFromPath(path);
    return { slug, frontmatter: normalizeFrontmatter(fm, slug) };
  }),
);

const BY_SLUG = new Map(ALL.map((p) => [p.slug, p]));

/** All published posts, newest first, without their compiled bodies. */
export function listPostMeta(): BlogPostMeta[] {
  return ALL;
}

/** Look up one published post's metadata by slug. */
export function getPostMeta(slug: string): BlogPostMeta | undefined {
  return BY_SLUG.get(slug);
}

/** Canonical URL for a post. */
export function postUrl(slug: string): string {
  return `https://getanyapi.com/blog/${slug}`;
}

const SITE_ORIGIN = 'https://getanyapi.com';

/**
 * Absolute URL for an OG/hero image. Frontmatter stores a root-relative path
 * (so the in-page <img> works in dev and prod); OG/Twitter/JSON-LD need it
 * absolute. Passes through full URLs and falls back to the site default card.
 */
export function ogImageUrl(path?: string): string {
  if (!path) return `${SITE_ORIGIN}/og-v9.png`;
  if (path.startsWith('http')) return path;
  return `${SITE_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
}
